import React, { useCallback, useEffect, useState } from "react";
import { fetchMenuEngineering, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

const MS = 1000;
const ET_MS = 3 * 3600 * MS;
const localStr = (ms) => new Date(ms + ET_MS).toISOString().slice(0, 16);
const startOfLocalDay = (ms) => localStr(ms).slice(0, 10) + "T00:00";

// Menu engineering (Kasavana-Smith): each sold dish plotted by margin and
// popularity into Stars / Plowhorses / Puzzles / Dogs, with the action each
// class implies. Read-only — no new data entry; depends on recipes being set.
const CLASS = {
  star:      { label: "Star",      color: "#1D7A44", bg: "#e7f6ec", action: "Feature & protect — keep quality, promote it." },
  plowhorse: { label: "Plowhorse", color: "#854F0B", bg: "#FBF0DA", action: "Popular but thin — raise price or cut recipe cost." },
  puzzle:    { label: "Puzzle",    color: "#185FA5", bg: "#E6F1FB", action: "Profitable but unloved — reposition, rename, promote." },
  dog:       { label: "Dog",       color: "#A32D2D", bg: "#FCEBEB", action: "Weak on both — rework or remove." },
};
const ORDER = ["star", "plowhorse", "puzzle", "dog"];

export default function MenuEngineeringPage() {
  const { t } = useT();
  // The i18n lookup returns the key itself when a string is missing, so a plain
  // `t(k) || default` never falls back. This treats "key unchanged" as missing
  // and uses the English default (AM/OM strings come in the language review).
  const tt = (k, d) => { const v = t(k); return (v && v !== k) ? v : d; };
  const cur = getCurrency() || "ETB";
  const now = Date.now();
  const [start, setStart] = useState(startOfLocalDay(now - 6 * 24 * 3600 * MS));
  const [end, setEnd] = useState(localStr(now));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try { setData(await fetchMenuEngineering({ start, end })); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not load."); }
    finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const preset = (which) => {
    const n = Date.now();
    if (which === "today") { setStart(startOfLocalDay(n)); setEnd(localStr(n)); }
    else if (which === "7d") { setStart(startOfLocalDay(n - 6 * 24 * 3600 * MS)); setEnd(localStr(n)); }
    else if (which === "30d") { setStart(startOfLocalDay(n - 29 * 24 * 3600 * MS)); setEnd(localStr(n)); }
  };

  const money = (v) => formatPrice(v || 0, cur);
  const sum = data && data.summary;
  const COLS = [
    ["menueng_dish", "Dish", "left"], ["menueng_sold", "Sold", "right"],
    ["menueng_price", "Price", "right"], ["menueng_cost", "Recipe cost", "right"],
    ["menueng_margin", "Margin", "right"], ["menueng_fcpct", "Food %", "right"],
    ["menueng_contribution", "Contribution", "right"], ["menueng_class", "Class", "center"],
  ];

  return (
    <>
      <h1>🍽️ {tt("menueng_title", "Menu engineering")} <span className="muted" style={{ fontSize: 14 }}>({tt("menueng_subtitle", "which dishes make money")})</span></h1>
      <p className="muted" style={{ marginTop: -4, maxWidth: 680 }}>
        {tt("menueng_intro", "Every dish placed by profit (price − recipe cost) and popularity. Stars earn and sell; plowhorses sell but earn little; puzzles earn but don't sell; dogs do neither.")}
      </p>

      <div className="card" style={{ marginTop: 10, padding: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{tt("cc_from", "From")}<br />
          <input type="datetime-local" value={start} max={end} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{tt("cc_to", "To")}<br />
          <input type="datetime-local" value={end} min={start} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="btn secondary small" onClick={() => preset("today")}>{tt("cc_today", "Today")}</button>
          <button className="btn secondary small" onClick={() => preset("7d")}>{tt("cc_last_7_days", "7 days")}</button>
          <button className="btn secondary small" onClick={() => preset("30d")}>30 days</button>
        </div>
      </div>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
      {loading ? <p className="muted" style={{ marginTop: 12 }}>{tt("loading", "Loading…")}</p> : data ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px,1fr))", gap: 12, marginTop: 12, maxWidth: 760 }}>
            {ORDER.map((k) => (
              <div key={k} className="card" style={{ padding: "12px 14px", background: CLASS[k].bg }}>
                <div style={{ fontSize: 12, color: CLASS[k].color, fontWeight: 700 }}>{CLASS[k].label}s</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: CLASS[k].color }}>{sum ? sum.counts[k] : 0}</div>
              </div>
            ))}
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 6, maxWidth: 760 }}>
            {tt("menueng_avg_margin", "Menu average margin")}: <b>{money(sum && sum.avg_contribution_margin)}</b> · {tt("menueng_pop_line", "popularity line")}: <b>{sum && sum.popularity_threshold}</b> {tt("menueng_units", "units")} · {sum && sum.dishes} {tt("menueng_dishes", "dishes")}
          </p>

          {data.uncosted && data.uncosted.length > 0 && (
            <div className="card" style={{ marginTop: 8, padding: 12, background: "#FFF8EC", borderColor: "#f0d38b", fontSize: 13, maxWidth: 760 }}>
              ⚠ {tt("menueng_uncosted", "Sold dishes with no recipe (can't be costed, excluded)")}: {data.uncosted.slice(0, 10).map((u) => u.name).join(", ")}{data.uncosted.length > 10 ? ` +${data.uncosted.length - 10}` : ""}
            </div>
          )}

          <div className="card" style={{ marginTop: 12, overflowX: "auto", maxWidth: 900 }}>
            {data.rows.length === 0 ? <p className="muted">{tt("menueng_no_rows", "No sales in this range.")}</p> : (
              <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ background: "#008080" }}>
                  {COLS.map(([k, d, align]) => (
                    <th key={k} style={{ color: "#fff", textAlign: align, padding: "8px 10px" }}>{tt(k, d)}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.menu_id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                      <td style={{ padding: "6px 10px" }}><b>{r.name}</b></td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{r.qty_sold}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{money(r.price)}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{money(r.recipe_cost)}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{money(r.unit_margin)}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: r.food_cost_pct > 40 ? "#A32D2D" : r.food_cost_pct > 33 ? "#854F0B" : "#1D7A44" }}>{r.food_cost_pct == null ? "—" : `${r.food_cost_pct}%`}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600 }}>{money(r.total_contribution)}</td>
                      <td style={{ padding: "6px 10px", textAlign: "center" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 9px", borderRadius: 10, background: CLASS[r.classification].bg, color: CLASS[r.classification].color }}>
                          {CLASS[r.classification].label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card" style={{ marginTop: 12, padding: 12, maxWidth: 760 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#063b48", marginBottom: 6 }}>{tt("menueng_what_to_do", "What to do")}</div>
            {ORDER.map((k) => (
              <div key={k} style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 4, fontSize: 13 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: CLASS[k].bg, color: CLASS[k].color, whiteSpace: "nowrap" }}>{CLASS[k].label}</span>
                <span className="muted">{CLASS[k].action}</span>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </>
  );
}
